"use client";

import { useActiveFeatureFlags, usePostHog } from "@posthog/next";
import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import FeatureAnnouncementCard from "@/components/common/FeatureAnnouncementCard";
import { FEATURE_ANNOUNCEMENTS } from "@/constants/featureAnnouncements";
import { useGailDock } from "@/contexts/chat/GailDockContext";
import { useFeatureAnnouncement } from "@/hooks/useFeatureAnnouncement";
import { useFloatingClearance } from "@/hooks/useFloatingClearance";
import useScreenSize from "@/hooks/useScreenSize";
import { triggerRealtimeModelUpgrade } from "@/services/realtimeModel/triggerRealtimeModelUpgrade";
import type {
  FeatureAnnouncement,
  FeatureAnnouncementSideEffect,
} from "@/types/featureAnnouncement";
import {
  getFeatureAnnouncementSurfaces,
  resolveFeatureAnnouncementNavigation,
  selectFeatureAnnouncement,
} from "@/utils/featureAnnouncements/featureAnnouncements";

const NO_FLAGS: readonly string[] = [];

const PRIMARY_SIDE_EFFECTS: Record<
  FeatureAnnouncementSideEffect,
  (posthog: ReturnType<typeof usePostHog>) => void
> = {
  realtimeModelUpgrade: triggerRealtimeModelUpgrade,
};

interface FeatureAnnouncementHostProps {
  announcements?: readonly FeatureAnnouncement[];
}

export default function FeatureAnnouncementHost({
  announcements = FEATURE_ANNOUNCEMENTS,
}: Readonly<FeatureAnnouncementHostProps>) {
  const pathname = usePathname();
  const router = useRouter();
  const activeFlags = useActiveFeatureFlags();
  const posthog = usePostHog();
  const { isOpen: isDockOpen } = useGailDock();
  const { isMobile } = useScreenSize();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Registry entries are stable references, so the result is stable without memoizing.
  const announcement = selectFeatureAnnouncement(
    announcements,
    getFeatureAnnouncementSurfaces(pathname),
    isMounted ? activeFlags ?? NO_FLAGS : NO_FLAGS
  );

  const { t } = useTranslation(announcement?.namespace ?? "common");
  const { isVisible, dismiss, accept } = useFeatureAnnouncement(announcement, {
    suppressed: isMobile && isDockOpen,
  });
  const bottomOffset = useFloatingClearance(isVisible);

  const handlePrimary = useCallback(() => {
    if (announcement?.primarySideEffect) PRIMARY_SIDE_EFFECTS[announcement.primarySideEffect](posthog);
    accept();
    if (!announcement?.primaryHref) return;
    const navigation = resolveFeatureAnnouncementNavigation(announcement.primaryHref, pathname ?? "/");
    if (navigation.kind === "external") {
      window.open(navigation.href, "_blank", "noopener,noreferrer");
    } else {
      router[navigation.kind](navigation.href);
    }
  }, [accept, announcement, pathname, posthog, router]);

  if (!announcement) return null;

  return (
    <FeatureAnnouncementCard
      isVisible={isVisible}
      title={t(announcement.titleKey)}
      description={t(announcement.descriptionKey)}
      primaryLabel={t(announcement.primaryLabelKey)}
      media={announcement.media}
      onPrimary={handlePrimary}
      onDismiss={dismiss}
      bottomOffset={bottomOffset}
    />
  );
}
